        import type { BaseAPIResponse } from '~/types/api';
import type { AssignLicensePayload, PurchaseLicenseFor, RefundLicenseResponse, StandardLicenseCostsResponse, UpgradePricingResponse } from '~/types/licenses/licenses';
import { ApiError } from '~/utils/errors';

export const licenseService = {
    async getLicenseCosts(): Promise<StandardLicenseCostsResponse> {
        try {
            const { data, status, message } = await usePbApi<StandardLicenseCostsResponse>('/me/licenses/standard-costs');
            
            if (status !== 200) {
                throw new ApiError(message || 'Failed to fetch license costs', status);
            }
            
            if (!data) {
                throw new ApiError('No license costs data received', 404);
            }
            
            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async assignLicense(personId: number, licenseId: number, contact: PurchaseLicenseFor): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        const payload: AssignLicensePayload = {
            assigned_to_id: contact.id,
            type: contact.type
        };

        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.licenses.update_license(personId, licenseId), 
                {
                    method: 'PATCH',
                    body: JSON.stringify(payload),
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to assign license', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async refundLicense(personId: number, licenseId: number, payload: { reason: string }): Promise<BaseAPIResponse<RefundLicenseResponse>> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<RefundLicenseResponse>>(
                nuxtApp.$api.routes.people.licenses.refund(personId, licenseId),
                {
                    method: 'DELETE',
                    body: JSON.stringify(payload),
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to refund license', status);
            }

            if (!data) {
                throw new ApiError('No refund data received', 404);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updateLicense(personId: number, licenseId: number, payload: { renew: boolean }): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.licenses.update_license(personId, licenseId),
                {
                    method: 'PATCH',
                    body: JSON.stringify(payload),
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to update license', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async unassignLicense(personId: number, licenseId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        const payload = {
            assigned_to_id: null
        };

        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.licenses.update_license(personId, licenseId), 
                {
                    method: 'PATCH',
                    body: JSON.stringify(payload),
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to unassign license', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async purchaseLicenses(personId: number, purchaseData: any): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi(
                nuxtApp.$api.routes.people.licenses.purchase_licenses(personId),
                {
                    method: 'POST',
                    body: JSON.stringify(purchaseData),
                }
            );

            if (status !== 201 && status !== 200) {
                throw new ApiError(message || 'Failed to purchase licenses', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async confirmPayment(personId: number, paymentData: { payment_intent_id: string; invoice_id: string }): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi(
                nuxtApp.$api.routes.me.financial.confirmPayment(),
                {
                    method: 'POST',
                    body: JSON.stringify(paymentData),
                }
            );

            if (status !== 200 && status !== 201) {
                throw new ApiError(message || 'Failed to confirm payment', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    // License Downgrade at Renewal: cancel a pending scheduled change (DELETE on the same
    // change route). The license keeps renewing as its current plan.
    async cancelScheduledChange(personId: number, licenseId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.licenses.change_license(personId, licenseId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to cancel scheduled change', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async changeLicense(personId: number, licenseId: number, payload: { new_license: string; term: number; promo_code?: string }): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi(
                nuxtApp.$api.routes.people.licenses.change_license(personId, licenseId),
                {
                    method: 'PATCH',
                    body: JSON.stringify(payload),
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to change license', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async calculateUpgradePrice(personId: number, licenseId: number, payload: { new_license: string; term: number }): Promise<UpgradePricingResponse> {
        try {
            const { data, status, message } = await usePbApi<UpgradePricingResponse>(
                `/people/${personId}/licenses/${licenseId}/calculate-upgrade-price`,
                {
                    method: 'POST',
                    body: JSON.stringify(payload),
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to calculate upgrade price', status);
            }

            if (!data) {
                throw new ApiError('No pricing data received', 404);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },
}; 